import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { usePatientDetails } from '../../hooks/usePatientDetails';
import { useRoomProperties } from '../../hooks/useRoomProperties';
import { useHasBeenTrue } from '../../hooks/useHasBeenTrue';
import { useScrollLock } from '../../hooks/useScrollLock';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { PatientDetailsCard } from '../PatientDetails/PatientDetailsCard';
import { AuditLog } from '../PatientDetails/AuditLog';
import { PatientTreatmentsList } from '../PatientTreatmentsList/PatientTreatmentsList';
import { LoadError } from '../UI/LoadError';
import { LoadingPlaceholder } from '../UI/LoadingPlaceholder';
import { INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS } from '../InterventionGroupDrawer/interventionGroupDrawerConstants';
import styles from './PatientDetailsDrawer.module.css';

const EDIT_VIEW_SLIDE_MS = INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS;

interface PatientDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string | null;
  locationId: string | null;
  mode: 'view' | 'create';
  onPatientUpdated?: () => void;
}

// TODO: consolidate with InterventionGroupDrawer into a single drawer shell.
// TODO: replace close/back buttons with IconButton from qampo-ui-library
export const PatientDetailsDrawer = ({
  isOpen,
  onClose,
  patientId,
  locationId,
  mode,
  onPatientUpdated
}: PatientDetailsDrawerProps) => {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditSliding, setIsEditSliding] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  // `details` is null until this patient's own fetch lands — including right
  // after a switch to another patient — so it doubles as the load gate.
  // `isRefreshing` is the narrower case: a refresh of the patient already on
  // screen, where only the values in the view slot are known to be stale.
  const {
    data: details,
    isRefreshing,
    error: hasDetailsError
  } = usePatientDetails(locationId, patientId, refreshToken);

  // Only the *edit* form is gated on this: it resolves the patient's room needs
  // against the table, and an incomplete lookup silently drops them so a save
  // wipes them. Everything else here — the read-only card, treatments, audit
  // log, the create form — either cannot save room needs or has none to lose,
  // and room needs are optional, so none of it waits on this table.
  const {
    isLoaded: isRoomPropertiesLoaded,
    hasError: hasRoomPropertiesError,
    retry: retryRoomProperties
  } = useRoomProperties(isOpen);

  // Latched: once the table has arrived, a later `clearRoomProperties` must not
  // unmount an edit in progress and throw away what the user typed.
  const canEdit = useHasBeenTrue(isRoomPropertiesLoaded);

  useScrollLock(isOpen, drawerRef);

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen && !isOpen) {
    setPrevIsOpen(false);
    setIsEditing(false);
    setIsEditSliding(false);
  } else if (!prevIsOpen && isOpen) {
    setPrevIsOpen(true);
  }

  const handleEditStart = useCallback(() => {
    setIsEditing(true);
    requestAnimationFrame(() => setIsEditSliding(true));
  }, []);

  const handleEditEnd = useCallback(() => {
    setIsEditSliding(false);
    setTimeout(() => setIsEditing(false), EDIT_VIEW_SLIDE_MS);
  }, []);

  const handleClose = useCallback(() => {
    setIsEditing(false);
    setIsEditSliding(false);
    onClose();
  }, [onClose]);

  const handlePatientUpdated = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
    onPatientUpdated?.();
  }, [onPatientUpdated]);

  // The drawer is never unmounted by PatientsList — `isOpen` only toggles CSS —
  // so re-clicking the same patient changes no effect dependency. Bumping the
  // token is the only way back from a failed fetch.
  const handleRetryDetails = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          handleEditEnd();
        } else {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose, isEditing, handleEditEnd]);

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={handleClose} aria-hidden="true" />}
      <aside
        ref={drawerRef}
        className={`${styles.drawer} ${isOpen ? styles.open : ''}`}
        aria-label={t('details')}
      >
        {mode === 'create' && isOpen && locationId && (
          <>
            <div className={styles.drawerHeader}>
              <div>
                <span className={styles.drawerTitle}>{t('add-patient')}</span>
              </div>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label={t('close-details')}
              >
                &times;
              </button>
            </div>

            {/* Ungated: a new patient has no room needs to lose, and the field
                is optional, so a missing table must not block creation. */}
            <div className={styles.content}>
              <PatientDetailsCard
                locationId={locationId}
                mode="create"
                onEditStart={handleEditStart}
                onEditEnd={handleClose}
                onPatientUpdated={onPatientUpdated}
              />
            </div>
          </>
        )}
        {mode === 'view' && isOpen && locationId && (
          <>
            <div className={styles.drawerHeader}>
              <div>
                {isEditing && details ? (
                  <div className={styles.patientHeader}>
                    <button
                      className={styles.backButton}
                      onClick={handleEditEnd}
                      aria-label={t('go-back')}
                    >
                      <span className={styles.backIcon}>
                        <ChevronIcon />
                      </span>
                    </button>
                    <span className={styles.drawerTitle}>{details.profile.name}</span>
                  </div>
                ) : (
                  <span className={styles.drawerTitle}>{details?.profile.name ?? ''}</span>
                )}
              </div>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                aria-label={t('close-details')}
              >
                &times;
              </button>
            </div>

            <div className={styles.content}>
              {/* These three are mutually exclusive and jointly exhaustive: a
                  failed fetch offers a way out, an in-flight one shows the
                  placeholder, and otherwise the cards render. Without the error
                  branch the placeholder would be permanent — the drawer never
                  unmounts, so nothing else would ever retry. */}
              {!details && hasDetailsError && (
                <LoadError
                  message={t('patient-details-load-error')}
                  onRetry={handleRetryDetails}
                />
              )}
              {!details && !hasDetailsError && <LoadingPlaceholder />}
              {details && (
                <div className={styles.slideContainer}>
                  <div className={`${styles.mainView} ${isEditing ? styles.slideOut : ''}`}>
                    {/* Only this slot holds values a refresh can invalidate.
                        Shimmering it alone keeps the header, the slide
                        container and the edit card — and its form state —
                        mounted, so a save still slides out normally. */}
                    {isRefreshing ? (
                      <LoadingPlaceholder />
                    ) : (
                      <>
                        <PatientDetailsCard
                          patient={details.profile}
                          locationId={locationId}
                          mode="view"
                          onEditStart={handleEditStart}
                          onEditEnd={handleEditEnd}
                          onPatientUpdated={handlePatientUpdated}
                        />

                        <PatientTreatmentsList treatments={details.treatmentPlans} />

                        <AuditLog entries={details.auditLog} />
                      </>
                    )}
                  </div>

                  {isEditing && (
                    <div
                      className={`${styles.editView} ${isEditSliding ? styles.slideIn : ''}`}
                      style={{ '--edit-view-slide-ms': `${EDIT_VIEW_SLIDE_MS}ms` } as CSSProperties}
                    >
                      {/* The one place room needs can be destroyed: seeding
                          this form from an unloaded table would send an empty
                          list on save. Latched, so it cannot be pulled out from
                          under an edit already in progress. */}
                      {canEdit && (
                        <PatientDetailsCard
                          patient={details.profile}
                          locationId={locationId}
                          mode="edit"
                          onEditStart={handleEditStart}
                          onEditEnd={handleEditEnd}
                          onPatientUpdated={handlePatientUpdated}
                        />
                      )}
                      {!canEdit && hasRoomPropertiesError && (
                        <LoadError
                          message={t('room-properties-load-error')}
                          onRetry={retryRoomProperties}
                        />
                      )}
                      {!canEdit && !hasRoomPropertiesError && <LoadingPlaceholder />}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
};
